import React, { FC, createContext, useCallback, useEffect, useState } from 'react';
import safeLocalStorage from '@modules/utilities/utils/safeLocalStorage';

export type StoredSentPrompt = { prompt: string; ts: number };
export type StoredSentPrompts = Record<string, StoredSentPrompt>;
export type SentPrompts = Record<string, string>;

export const SendToAssistantContext = createContext<{
  sendToAssistant: (id: string, prompt: string) => void;
  clearSentPrompt: (id: string) => void;
  sentPrompts: SentPrompts;
}>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sendToAssistant: (id, prompt) => {
    throw new Error('Not implemented');
  },
  clearSentPrompt: (id) => {
    throw new Error('Not implemented');
  },
  sentPrompts: {},
});

const LOCALSTORAGE_KEY = 'CreatorDocumentation.SendToAssistant';
const LOCALSTORAGE_EXPIRY = 86_400_000; // 24 hours

const getLocalStorageState = (): StoredSentPrompts => {
  let storedSentPrompts: StoredSentPrompts | null = null;
  try {
    storedSentPrompts = JSON.parse(
      safeLocalStorage.getItem(LOCALSTORAGE_KEY),
    ) as StoredSentPrompts | null;
  } catch (error) {
    return {};
  }
  if (!storedSentPrompts) {
    return {};
  }
  // remove expired items
  return Object.fromEntries(
    Object.entries(storedSentPrompts).filter(
      ([_, { ts }]) => +new Date() - ts - LOCALSTORAGE_EXPIRY <= 0,
    ),
  ) as StoredSentPrompts;
};

const removeLocalStorageState = (id: string): void => {
  const oldState = getLocalStorageState();
  const { [id]: _, ...newState } = oldState;
  safeLocalStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(newState));
};

const setLocalStorageState = (id: string, prompt: string): void => {
  const oldState = getLocalStorageState();
  safeLocalStorage.setItem(
    LOCALSTORAGE_KEY,
    JSON.stringify({
      ...oldState,
      [id]: {
        prompt,
        ts: +new Date(),
      },
    }),
  );
};

export const SendToAssistantProvider: FC<React.PropsWithChildren> = ({ children }) => {
  // SSR-safe localStorage access: Initialize with empty object, then hydrate with actual value on client
  // This prevents "window is not defined" errors during SSR and avoids hydration mismatches
  const [sentPrompts, setSentPrompts] = useState<SentPrompts>({});
  useEffect(() => {
    const localStorageState = getLocalStorageState();
    setSentPrompts(
      Object.fromEntries(Object.entries(localStorageState).map(([id, { prompt }]) => [id, prompt])),
    );
  }, []);

  const sendToAssistant = useCallback(
    (id: string, prompt: string) => {
      setSentPrompts({ ...sentPrompts, [id]: prompt });
      setLocalStorageState(id, prompt);
    },
    [setSentPrompts, sentPrompts],
  );

  const clearSentPrompt = useCallback(
    (id: string) => {
      const { [id]: _, ...newState } = sentPrompts;
      setSentPrompts(newState);
      removeLocalStorageState(id);
    },
    [setSentPrompts, sentPrompts],
  );

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <SendToAssistantContext.Provider value={{ sendToAssistant, clearSentPrompt, sentPrompts }}>
      {children}
    </SendToAssistantContext.Provider>
  );
};
